<script setup lang="ts">
import { Heading, Text } from '~ds/components/ui/typography';

import type { VideoSpeaker } from '~/types/Learn/Video';

interface Props {
  speakers: VideoSpeaker[];
}

defineProps<Props>();

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]!.toUpperCase()).join('');
</script>

<template>
  <section
    v-if="speakers.length > 0"
    class="flex flex-col gap-4"
    data-testid="video-speakers"
  >
    <Heading as="h2" size="md">
      {{ $t('unified.videos.speakers') }}
    </Heading>
    <ul class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <li
        v-for="(speaker, index) in speakers"
        :key="index"
        class="flex flex-col gap-3 rounded-3xl bg-card p-4"
      >
        <div class="flex items-center gap-2">
          <NuxtImg
            v-if="speaker.imageUrl"
            :src="speaker.imageUrl"
            alt=""
            loading="lazy"
            width="88"
            class="size-11 shrink-0 rounded-full object-cover"
          />
          <span
            v-else
            aria-hidden="true"
            data-testid="video-speaker-initials"
            class="flex size-11 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold"
          >{{ initials(speaker.name) }}</span>
          <div class="flex min-w-0 flex-col gap-2">
            <Text weight="semibold">
              {{ speaker.name }}
            </Text>
            <Text
              v-if="speaker.organisation"
              size="xs"
              class="text-muted-foreground"
            >
              {{ speaker.organisation }}
            </Text>
          </div>
        </div>
        <Text v-if="speaker.bio" size="xs">
          {{ speaker.bio }}
        </Text>
      </li>
    </ul>
  </section>
</template>
